import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Public as PublicIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { useAuth } from "../hooks/useAuth";
import AppService from "../services/AppService";
import ChecklistService from "../services/ChecklistService";
import { App } from "../types/app";
import { ChecklistProgress, ChecklistItem } from "../types/checklist";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const DeploymentChecklist: React.FC = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [progress, setProgress] = useState<ChecklistProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    if (user) {
      loadApps();
    }
  }, [user]);

  useEffect(() => {
    if (selectedApp && user) {
      loadProgress();
    }
  }, [selectedApp, user]);

  const loadApps = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const userApps = await AppService.getUserApps(user.email);
      setApps(userApps);
    } catch (err) {
      setError("Failed to load apps");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    if (!user || !selectedApp) return;

    try {
      setLoading(true);
      const checklistProgress = await ChecklistService.getOrCreateProgress(
        selectedApp.appId,
        user.email,
      );
      setProgress(checklistProgress);
      setError("");
    } catch (err) {
      setError("Failed to load checklist");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleItemToggle = async (itemId: string, currentStatus: boolean) => {
    if (!user || !selectedApp) return;

    try {
      await ChecklistService.updateItemStatus(
        selectedApp.appId,
        user.email,
        itemId,
        !currentStatus,
      );
      await loadProgress();
    } catch (err) {
      setError("Failed to update checklist");
      console.error(err);
    }
  };

  const handleResetConfirm = async () => {
    if (!user || !selectedApp) return;

    try {
      setLoading(true);
      await ChecklistService.resetChecklist(selectedApp.appId, user.email);
      await loadProgress();
      setResetDialogOpen(false);
    } catch (err) {
      setError("Failed to reset checklist");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePanelExpand = (panelId: string) => {
    setExpandedPanels({
      ...expandedPanels,
      [panelId]: !expandedPanels[panelId],
    });
  };

  const groupItemsByCategory = (items: ChecklistItem[], platform: string) => {
    const grouped: Record<string, Record<string, ChecklistItem[]>> = {};

    items
      .filter((item) => item.platform === platform)
      .forEach((item) => {
        if (!grouped[item.category]) {
          grouped[item.category] = {};
        }
        if (!grouped[item.category][item.subcategory]) {
          grouped[item.category][item.subcategory] = [];
        }
        grouped[item.category][item.subcategory].push(item);
      });

    return grouped;
  };

  const getCategoryProgress = (
    items: ChecklistItem[],
    category: string,
    platform: string,
  ): number => {
    const categoryItems = items.filter(
      (item) => item.category === category && item.platform === platform,
    );
    if (categoryItems.length === 0) return 0;
    const completed = categoryItems.filter((item) => item.completed).length;
    return Math.round((completed / categoryItems.length) * 100);
  };

  const renderChecklistSection = (
    platform: "android" | "ios" | "cross-platform",
  ) => {
    if (!progress) return null;

    const grouped = groupItemsByCategory(progress.items, platform);

    return (
      <Box>
        {Object.entries(grouped).map(([category, subcategories]) => {
          const panelId = `${platform}_${category}`;
          const categoryProgress = getCategoryProgress(
            progress.items,
            category,
            platform,
          );

          return (
            <Accordion
              key={panelId}
              expanded={expandedPanels[panelId] || false}
              onChange={() => handlePanelExpand(panelId)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    pr: 2,
                  }}
                >
                  <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>
                    {category}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={`${categoryProgress}%`}
                      size="small"
                      color={categoryProgress === 100 ? "success" : "default"}
                    />
                    <Box sx={{ width: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={categoryProgress}
                        color={categoryProgress === 100 ? "success" : "primary"}
                      />
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {Object.entries(subcategories).map(([subcategory, items]) => (
                  <Box key={subcategory} sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}
                    >
                      {subcategory}
                    </Typography>
                    {items.map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={item.completed}
                            onChange={() =>
                              handleItemToggle(item.id, item.completed)
                            }
                            color="success"
                          />
                        }
                        label={item.text}
                        sx={{
                          display: "block",
                          mb: 0.5,
                          "& .MuiFormControlLabel-label": {
                            textDecoration: item.completed
                              ? "line-through"
                              : "none",
                            color: item.completed ? "#999" : "inherit",
                          },
                        }}
                      />
                    ))}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
          <AssignmentIcon
            sx={{ mr: 2, verticalAlign: "middle", fontSize: "2rem" }}
          />
          Deployment Checklist
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Track your app deployment progress with comprehensive checklists
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* App Selection & Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Select a registered app</InputLabel>
                <Select
                  value={selectedApp?.appId || ""}
                  label="Select a registered app"
                  onChange={(e) => {
                    const app = apps.find((a) => a.appId === e.target.value);
                    setSelectedApp(app || null);
                  }}
                >
                  <MenuItem value="">
                    <em>Select a registered app</em>
                  </MenuItem>
                  {apps.map((app) => (
                    <MenuItem key={app.appId} value={app.appId}>
                      {app.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setResetDialogOpen(true)}
                disabled={!selectedApp || loading}
                fullWidth
              >
                Reset Checklist
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Progress Overview */}
      {progress && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ p: 2, textAlign: "center", backgroundColor: "action.hover" }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                Overall Progress
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                {progress.overallProgress}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.overallProgress}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ p: 2, textAlign: "center", backgroundColor: "#e8f5e9" }}
            >
              <AndroidIcon sx={{ fontSize: "2rem", color: "#3DDC84", mb: 1 }} />
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                Android
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {progress.androidProgress}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ p: 2, textAlign: "center", backgroundColor: "#f3e5f5" }}
            >
              <AppleIcon sx={{ fontSize: "2rem", mb: 1 }} />
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                iOS
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {progress.iosProgress}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper
              sx={{ p: 2, textAlign: "center", backgroundColor: "#fff3e0" }}
            >
              <PublicIcon sx={{ fontSize: "2rem", mb: 1 }} />
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                Cross-Platform
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {progress.crossPlatformProgress}%
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Checklist Content */}
      {loading && !progress ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !selectedApp ? (
        <Paper sx={{ p: 6, textAlign: "center", backgroundColor: "action.hover" }}>
          <AssignmentIcon sx={{ fontSize: "4rem", color: "#bdbdbd", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
            No app selected
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Select an app above to view its deployment checklist
          </Typography>
        </Paper>
      ) : progress ? (
        <Paper sx={{ p: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(e, v) => setTabValue(v)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              icon={<AndroidIcon />}
              label="Android"
              iconPosition="start"
              sx={{ textTransform: "none" }}
            />
            <Tab
              icon={<AppleIcon />}
              label="iOS"
              iconPosition="start"
              sx={{ textTransform: "none" }}
            />
            <Tab
              icon={<PublicIcon />}
              label="Cross-Platform"
              iconPosition="start"
              sx={{ textTransform: "none" }}
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            <TabPanel value={tabValue} index={0}>
              {renderChecklistSection("android")}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderChecklistSection("ios")}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderChecklistSection("cross-platform")}
            </TabPanel>
          </Box>
        </Paper>
      ) : null}

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Checklist</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset the checklist for{" "}
            <strong>{selectedApp?.name}</strong>? All progress will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeploymentChecklist;
